import React, { useContext, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Avatar from '@atlaskit/avatar';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { AtlassianIcon } from '@atlaskit/logo';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Pressable from '@atlaskit/primitives/pressable';
import { token } from '@atlaskit/tokens';

import { EditorContextPanelContext } from '@confluence/editor-context-panel/entry-points/EditorContextPanelContext.ts';

import { useInsertSnippet } from '../hooks/useInsertSnippet.ts';
import {
	ATLASSIAN_ACCOUNT_ID,
	type Snippet,
	type SnippetOwner,
	type UserSnippetOwner,
} from '../snippetTypes.ts';
import { SnippetPreview } from '../preview/SnippetPreview.tsx';

import { SnippetPanelActions } from './SnippetPanelActions.tsx';
import { useSnippetPopupPreview } from './SnippetPopupPreviewContext.tsx';
import { SnippetPreviewSkeleton } from './skeleton/SnippetPreviewSkeleton.tsx';

const SNIPPET_PREVIEW_WIDTH = 140;
const SNIPPET_PREVIEW_HEIGHT = 80;
const EDITOR_PRE_SCALE_WIDTH = 800;
const PREVIEW_PADDING_BLOCK = token('space.075');
const PREVIEW_PADDING_INLINE = token('space.100');

const styles = cssMap({
	cardWrapper: {
		position: 'relative',
		width: '100%',
		height: '100px',
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
	},
	cardWrapperActive: {
		backgroundColor: token('elevation.surface.hovered'),
	},
	cardButton: {
		textAlign: 'left',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		width: '100%',
		height: '100%',
		backgroundColor: 'transparent',
	},
	actionsWrapper: {
		position: 'absolute',
		top: token('space.100'),
		right: token('space.100'),
		zIndex: 1,
	},
	preview: {
		width: `${SNIPPET_PREVIEW_WIDTH}px`,
		minWidth: `${SNIPPET_PREVIEW_WIDTH}px`,
		height: `${SNIPPET_PREVIEW_HEIGHT}px`,
		borderRadius: token('radius.large'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		overflow: 'hidden',
		backgroundColor: 'transparent',
		paddingBlock: PREVIEW_PADDING_BLOCK,
		paddingInline: PREVIEW_PADDING_INLINE,
	},
	titleContainer: {
		flex: 1,
		textAlign: 'left',
		// Right padding to prevent overlap with absolutely positioned actions (32px button width)
		paddingRight: token('space.500'),
	},
});

export const messages = defineMessages({
	snippetUses: {
		id: 'snippets.panel.item.uses',
		defaultMessage: 'Uses',
		description: 'Shows the number of uses for a specific Block Templates',
	},
	snippetCardAriaLabel: {
		id: 'snippets.panel.item.card.aria-label',
		defaultMessage: 'Block Template: {name}',
		description: 'Label for block template card button',
	},
	snippetPreview: {
		id: 'snippets.panel.item.preview',
		defaultMessage: 'Preview',
		description: 'Text shown in the preview area of a block template card',
	},
});

type SnippetPanelItemProps = {
	snippet: Snippet;
	contentId: string;
	spaceId: string;
	spaceKey: string;
};

export const SnippetPanelItem = ({
	snippet,
	contentId,
	spaceId,
	spaceKey,
}: SnippetPanelItemProps) => {
	const [isHovered, setIsHovered] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const { editorAPI } = useContext(EditorContextPanelContext);
	const insertSnippet = useInsertSnippet(editorAPI);
	const { showPreview } = useSnippetPopupPreview();

	const handleMouseEnter = (event: React.MouseEvent) => {
		setIsHovered(true);
		showPreview(snippet, event.currentTarget.getBoundingClientRect());
	};
	const handleMouseLeave = () => setIsHovered(false);
	const handleMenuOpenChange = (isOpen: boolean) => setIsMenuOpen(isOpen);
	const onClick = () => insertSnippet(snippet);

	return (
		<Box
			xcss={cx(styles.cardWrapper, isMenuOpen && styles.cardWrapperActive)}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			<SnippetCardButton snippet={snippet} onClick={onClick}>
				<Inline alignInline="start" alignBlock="center" space="space.150">
					<SnippetPreviewContainer snippet={snippet} />
					<SnippetInfo snippet={snippet} />
				</Inline>
			</SnippetCardButton>
			<Box xcss={styles.actionsWrapper}>
				<SnippetPanelActions
					snippet={snippet}
					isHovered={isHovered}
					onMenuOpenChange={handleMenuOpenChange}
					contentId={contentId}
					spaceId={spaceId}
					spaceKey={spaceKey}
				/>
			</Box>
		</Box>
	);
};

const SnippetCardButton = ({
	children,
	snippet,
	onClick,
}: {
	onClick: () => void;
	children: React.ReactNode;
	snippet: Snippet;
}) => {
	const intl = useIntl();

	return (
		<Pressable
			xcss={styles.cardButton}
			onClick={onClick}
			testId={`snippet-card-button-${snippet.snippetId}`}
			aria-label={intl.formatMessage(messages.snippetCardAriaLabel, { name: snippet.name })}
		>
			{children}
		</Pressable>
	);
};

const SnippetPreviewContainer = ({ snippet }: { snippet: Snippet }) => {
	return (
		<Box xcss={styles.preview}>
			<SnippetPreview
				snippet={snippet}
				dimensions={{
					width: `calc(${SNIPPET_PREVIEW_WIDTH}px - (${PREVIEW_PADDING_INLINE} * 2))`,
					maxHeight: `calc(${SNIPPET_PREVIEW_HEIGHT}px - (${PREVIEW_PADDING_BLOCK} * 2))`,
				}}
				renderWidth={EDITOR_PRE_SCALE_WIDTH}
				loadingComponent={<SnippetPreviewSkeleton />}
			/>
		</Box>
	);
};

const CreatedByAvatar = ({ snippetOwner }: { snippetOwner: SnippetOwner }) => {
	const shouldUseAtlassianIcon = snippetOwner.accountId.toLowerCase() === ATLASSIAN_ACCOUNT_ID;
	if (shouldUseAtlassianIcon) {
		return <AtlassianIcon appearance="brand" size="xsmall" />;
	}

	return (
		<Avatar
			appearance="circle"
			size="xsmall"
			src={(snippetOwner as UserSnippetOwner).avatarUrl}
			name={snippetOwner.displayName}
		/>
	);
};

const SnippetInfo = ({ snippet }: { snippet: Snippet }) => {
	return (
		<Stack alignInline="start" alignBlock="start" space="space.075" grow="fill">
			<Box xcss={styles.titleContainer}>
				<Heading size="xsmall">
					<Text maxLines={3} weight="bold">
						{snippet.name}
					</Text>
				</Heading>
			</Box>
			<Inline separator="•">
				<Inline alignInline="center" alignBlock="center" space="space.050">
					<CreatedByAvatar snippetOwner={snippet.createdBy} />
					<Text color="color.text.subtlest" size="small" maxLines={1}>
						{snippet.createdBy.displayName}
					</Text>
				</Inline>
			</Inline>
		</Stack>
	);
};
